import { jsx as _jsx } from "react/jsx-runtime";
import { produce } from 'immer';
import { createContext, useContext, useReducer, useMemo, useEffect, } from 'react';
const accordionContext = createContext(null);
const reducer = produce((draft, actions) => {
    switch (actions.type) {
        case 'TOGGLE': {
            const item = getItem(actions.payload, draft.items);
            if (!item) {
                draft.items.push({ isOpen: true, title: actions.payload });
                return draft;
            }
            item.isOpen = !item.isOpen;
            return draft;
        }
        case 'CLEAR': {
            const item = getItem(actions.payload, draft.items);
            if (!item) {
                draft.items.push({ isOpen: true, title: actions.payload });
                return draft;
            }
            for (const element of draft.items) {
                element.isOpen = element.title === item.title;
            }
            return draft;
        }
        case 'REMOVE_ITEM': {
            draft.items = draft.items.filter((element) => element.title !== actions.payload);
            return draft;
        }
        case 'CREATE_ITEM': {
            draft.items.push(actions.payload);
            return draft;
        }
        case 'CHANGE_ITEM': {
            const item = getItem(actions.payload.title, draft.items);
            if (item) {
                item.isOpen = actions.payload.isOpen;
            }
            return draft;
        }
        default: {
            return draft;
        }
    }
});
export function useToggleAccordion() {
    const context = useContext(accordionContext);
    if (!context) {
        throw new Error('AccordionContext was not found');
    }
    const [, utils] = context;
    return useMemo(() => {
        return {
            open: (title) => utils.change(title, true),
            close: (title) => utils.change(title, false),
        };
    }, [utils]);
}
export function useAccordionContext(title, defaultOpened) {
    const context = useContext(accordionContext);
    if (!context) {
        throw new Error('AccordionContext was not found');
    }
    const [state, utils] = context;
    useEffect(() => {
        utils.create(title, defaultOpened);
        return () => utils.remove(title);
    }, [defaultOpened, title, utils]);
    const item = getItem(title, state.items);
    return useMemo(() => ({
        item,
        utils: {
            clear: () => utils.clear(title),
            toggle: () => utils.toggle(title),
            remove: () => utils.remove(title),
            create: (defaultOpened) => utils.create(title, defaultOpened),
        },
    }), [item, title, utils]);
}
export function AccordionProvider(props) {
    const [state, dispatch] = useReducer(reducer, {
        items: [],
    });
    const utils = useMemo(() => ({
        change: (title, isOpen) => {
            return dispatch({
                type: 'CHANGE_ITEM',
                payload: { isOpen, title },
            });
        },
        clear: (except) => {
            return dispatch({ type: 'CLEAR', payload: except });
        },
        toggle: (title) => {
            return dispatch({ type: 'TOGGLE', payload: title });
        },
        remove: (title) => {
            return dispatch({ type: 'REMOVE_ITEM', payload: title });
        },
        create: (title, defaultOpened) => {
            const item = { title, isOpen: defaultOpened || false };
            dispatch({ type: 'CREATE_ITEM', payload: item });
            return item;
        },
    }), [dispatch]);
    const value = useMemo(() => {
        return [{ items: state.items }, utils];
    }, [state.items, utils]);
    return (_jsx(accordionContext.Provider, { value: value, children: props.children }));
}
function getItem(title, items) {
    const item = items.find((element) => element.title === title);
    return item;
}
//# sourceMappingURL=AccordionContext.js.map